import { useCallback, useImperativeHandle } from "react";
import { getFilterValue } from "../hooks/useFilterValue";
import { getAuthValue } from "../../authModule/hooks/useAuthValue";
import { formatDate } from "../../../helpers/dateHelper";
import jsPDF from "jspdf";
import { getFilterLabels } from "../observables/filterLabels$";
type PdfPlacement = { x: number; y: number; width: number; height: number };

const getImagePlacement = (
  pdfWidth: number,
  pdfHeight: number,
  imgWidth: number,
  imgHeight: number,
): PdfPlacement => {
  const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  const width = imgWidth * ratio;
  const height = imgHeight * ratio;
  return {
    x: (pdfWidth - width) / 2,
    y: (pdfHeight - height) / 2,
    width,
    height,
  };
};
export default function useDashboardExport({
  exportRef,
  screenshotRef,
  setIsExporting,
  takeScreenshot,
  filename = "orva-dashboard-export.pdf",
}: {
  exportRef: any;
  screenshotRef: React.RefObject<any>;
  setIsExporting: (val: boolean) => void;
  takeScreenshot: (el: any, options?: any) => Promise<string>;
  filename?: string;
}) {
  const toLabels = (ids: any, map: Record<string, string>) => {
    const arr = Array.isArray(ids) ? ids : [];
    const labels = arr.map((id) => map[id] ?? id).filter(Boolean);
    return labels.length ? labels.join(", ") : null;
  };

  const getUsername = (auth: any) => {
    const firstName = auth?.firstName ?? "";
    const lastName = auth?.LastName ?? ""; // keep as-is because your API uses LastName
    return `${firstName} ${lastName}`.trim() || "Unknown";
  };

  const getReadableDateRange = (filters: any) => {
    const from = filters?.dateRange?.from;
    const to = filters?.dateRange?.to;
    if (!from || !to) return null;
    return `${formatDate(new Date(from))} - ${formatDate(new Date(to))}`;
  };

 const buildFilterLines = (
  filters: any,
  labels: any,
  toLabels: (ids: any, map: Record<string, string>) => string | null,
  readableDateRange: string | null,
) => {
  const lines: string[] = [];

  const pushIf = (condition: any, line: string | null | undefined) => {
    if (!condition) return;
    if (!line) return;
    lines.push(line);
  };

  const labelLine = (
    key: string,
    ids: any,
    map: Record<string, string>,
    prefix: string,
  ) => {
    const text = toLabels(ids, map);
    pushIf(Array.isArray(ids) && ids.length > 0, text ? `${prefix}: ${text}` : null);
  };

  // Includes
  pushIf(readableDateRange, `Date Range: ${readableDateRange}`);
  labelLine("ots", filters?.ots, labels.otById, "OTs");
  labelLine(
    "procedureIds",
    filters?.procedureIds,
    labels.procedureById,
    "Primary Procedures",
  );

  pushIf(
    Array.isArray(filters?.attendingSurgeonNames) &&
      filters.attendingSurgeonNames.length > 0,
    `Primary Surgeons: ${filters.attendingSurgeonNames.join(", ")}`,
  );

  labelLine("nurseIds", filters?.nurseIds, labels.nurseById, "Circulating Nurses");
  labelLine(
    "assignedAnesthesiologist",
    filters?.assignedAnesthesiologist,
    labels.anesthById,
    "Anesthesiologist",
  );
  labelLine(
    "caseStartDelay",
    filters?.caseStartDelay,
    labels.delayById,
    "Case Start Delay Categories",
  );

  pushIf(filters?.caseOrder, `Case Order: ${filters.caseOrder}`);
  pushIf(filters?.autoClosed, `Auto Closed: ${filters.autoClosed}`);
  pushIf(
    filters?.antibioticUsed,
    `Antibiotic Used: ${filters.antibioticUsed}`,
  );

  // Excludes
  labelLine("excludeOts", filters?.excludeOts, labels.otById, "Exclude OTs");
  labelLine(
    "excludeProcedureIds",
    filters?.excludeProcedureIds,
    labels.procedureById,
    "Exclude Procedures",
  );
  labelLine(
    "excludeNurseIds",
    filters?.excludeNurseIds,
    labels.nurseById,
    "Exclude Nurses",
  );
  labelLine(
    "excludeAssignedAnesthesiologist",
    filters?.excludeAssignedAnesthesiologist,
    labels.anesthById,
    "Exclude Anesthesiologists",
  );
  labelLine(
    "excludeDelayCategories",
    filters?.excludeDelayCategories,
    labels.delayById,
    "Exclude Delay Categories",
  );

  pushIf(filters?.excludeCaseOrder, `Exclude Case Order: ${filters.excludeCaseOrder}`);

  pushIf(
    Array.isArray(filters?.excludeCaseIds) && filters.excludeCaseIds.length > 0,
    `Exclude Case IDs: ${filters.excludeCaseIds.join(", ")}`,
  );

  return lines;
};

  const writeLinesToPdf = (
    pdf: jsPDF,
    lines: string[],
    margin = 40,
    lineSpacing = 20,
  ) => {
    let y = margin;
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    lines.forEach((line) => {
      pdf.text(line, margin, y);
      y += lineSpacing;
    });
  };

  const handleExportPDF = useCallback(
    (img: string) => {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4" as const,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const image = new window.Image();
      image.src = img;

      image.onload = () => {
        const placement = getImagePlacement(
          pdfWidth,
          pdfHeight,
          image.width,
          image.height,
        );

        pdf.addImage(
          img,
          "PNG",
          placement.x,
          placement.y,
          placement.width,
          placement.height,
        );
        pdf.addPage();

        const filters = getFilterValue();
        const auth = getAuthValue();
        const labels = getFilterLabels();

        const username = getUsername(auth);
        const timestamp = new Date().toLocaleString();
        const readableDateRange = getReadableDateRange(filters);

        const filterLines = buildFilterLines(
          filters,
          labels,
          toLabels,
          readableDateRange,
        );

        const lines = [
          `Export By: ${username}`,
          `Created Date: ${timestamp}`,
          ...(filterLines.length ? ["Filters:", ...filterLines] : []),
        ];

        writeLinesToPdf(pdf, lines);
        pdf.save(filename);
        setIsExporting(false);
      };

      image.onerror = () => {
        setIsExporting(false);
      };
    },
    [filename, setIsExporting],
  );

  useImperativeHandle(
    exportRef,
    () => ({
      async exportOverviewScreenshot() {
        if (!screenshotRef.current) return;
        setIsExporting(true);
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const img = await takeScreenshot(screenshotRef.current, {
          backgroundColor: null,
          useCORS: true,
        });
        handleExportPDF(img);
      },
    }),
    [takeScreenshot, handleExportPDF, screenshotRef, setIsExporting],
  );
}
